import axios from 'axios';
import dayjs from 'dayjs';
import orderBy from 'lodash.orderby';
import * as Sentry from '@sentry/vue';
import throttle from 'lodash.throttle';
import tinycolor from 'tinycolor2/tinycolor';

import {deepCopyObject} from './utils';
import bus from './bus';
import environment from './environment';
import router from './router/index';
import socketbridge from './socketbridge';
import store from './store';

// Setup axios to send the CSRF token back to Django with every request.
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

var CancelToken = axios.CancelToken;

export const HTTP_200_OK = 200;
export const HTTP_202_ACCEPTED = 202;
const HTTP_401_UNAUTHORIZED = 401;
const HTTP_404_NOT_FOUND = 404;
const HTTP_503_SERVICE_UNAVAILABLE = 503;
const RESPONSE_404_FALLBACK_ROUTE = '/';

/**
 * Given the error in a Promise.catch() block, decide whether we need
 * to go to the fallback page, because the requested resource does not
 * exist.
 */
export function goToFallbackOn404(error) {
  if (!error || !error.response) {
    return;
  }
  if (error.response.status === HTTP_404_NOT_FOUND) {
    console.log('Falling back on 404 response');
    router.push(RESPONSE_404_FALLBACK_ROUTE);
  }
}

/**
 * Show alert to User only once during timeout, no matter how often called.
 */
const alertAccountDeactivatedThrottled = throttle(
  () => {
    bus.$emit('alert-warning', {
      message: 'accountDeactivated',
      translate: true,
      timeout: 8000,
    });
  },
  8000,
  {leading: true, trailing: false}
);

function handleHttpError(error) {
  if (axios.isCancel(error)) {
    return;
  }

  // 401
  if (error.response && error.response.status === HTTP_401_UNAUTHORIZED) {
    // If the account has been deactivated or deleted, show a specific
    // alert to the User.
    if (error.response.data && error.response.data.detail) {
      if (error.response.data.detail.indexOf('inactive or deleted') !== -1) {
        alertAccountDeactivatedThrottled();
      }
    }

    // Note: Force a logout, e.g. to make sure a user can login
    //   again after his token has expired.
    store.commit('UNSET_CURRENT_USER');

    const query = {redirect: router.currentRoute.path};
    if (router.currentRoute.path == '/login') {
      query.redirect = router.currentRoute.query.redirect;
    }
    if (router.currentRoute.query.externalredirect) {
      query.externalredirect = router.currentRoute.query.externalredirect;
    }
    router.push({
      path: '/login',
      query: query,
    });
  }

  // 503
  if (
    error.response &&
    error.response.status === HTTP_503_SERVICE_UNAVAILABLE
  ) {
    router.push('/maintenance');
  }
}

/**
 * Wrap axios HTTP calls to our internal API, handling authentication.
 *
 * If a user logs in, the received token is saved in localStorage and
 * reused for every subsequent request in here. Logging out will first
 * log out on the server (deleting the Token object) and then remove
 * the token on the client.
 */
class _API {
  constructor() {
    this.apiURL = environment.urls.api;
    this.updateProgress = true;

    // Prepare a token so we can cancel the next request.
    // https://github.com/mzabriskie/axios#cancellation
    this.cancelCurrentRequest();
  }

  get(url, opts) {
    return this.request('get', url, null, opts);
  }

  delete(url, payload, opts) {
    return this.request('delete', url, payload, opts);
  }

  post(url, payload, opts) {
    return this.request('post', url, payload, opts);
  }

  put(url, payload, opts) {
    return this.request('put', url, payload, opts);
  }

  patch(url, payload, opts) {
    return this.request('patch', url, payload, opts);
  }

  // See https://www.w3schools.com/js/js_cookies.asp

  getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      let cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i].trim();
        // Does this cookie string begin with the name we want?
        if (cookie.substring(0, name.length + 1) === name + '=') {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  setCookie(name, value, daysToExpire = 14) {
    let date = new Date();
    date.setTime(date.getTime() + daysToExpire * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();

    // Attention: These should match the Django settings TOKEN_COOKIE_SAMESITE,
    //  TOKEN_COOKIE_SECURE.
    const sameSite = 'SameSite=Lax';
    const secure = ''; // Empty, aka Secure=False

    const cookieStr =
      name + '=' + value + ';' + expires + ';path=/;' + sameSite + ';' + secure;
    document.cookie = cookieStr;

    this.notifyAppWrapperCookiesHaveChanged();
  }

  deleteCookie(name) {
    document.cookie = name + '=; Max-Age=0; path=/;';
    this.notifyAppWrapperCookiesHaveChanged();
  }

  retrieveGitCommitHash() {
    return API.get('revision');
  }

  /**
   * Login using credentials for a token we can store in localStorage.
   *
   * This token will be sent along every subsequent request to
   * authenticate the user from then on.
   */
  login(usernameOrEmail, password, code = null) {
    const credentials = {username: usernameOrEmail, password: password};
    if (code) {
      credentials.code = code;
    }
    const isEmail =
      usernameOrEmail.indexOf('@', usernameOrEmail) !== -1 &&
      usernameOrEmail.indexOf('.', usernameOrEmail) !== -1;
    if (isEmail) {
      credentials.email = usernameOrEmail;
      delete credentials.username;
    }

    const tokenAuth = false;
    const promise = this.request(
      'post',
      'token/login',
      credentials,
      null,
      tokenAuth
    );
    promise.then((response) => {
      if (response.data.coderequired) {
        return promise;
      }

      console.log('Logged in');
      const expireAfterOneYear = 365;
      this.setCookie('authtoken', response.data.token, expireAfterOneYear);

      socketbridge.connect();
    });

    return promise;
  }

  /**
   * Logout the user client-side only.
   *
   * However we do track the logout on the server-side of things, so
   * we wait for this until removing the token.
   *
   * Since we don't use Sessions in here, there is no need to logout on
   * the server unless we'd want to rotate Tokens. However this would
   * currently logout all browser-sessions for that User on all
   * devices.
   */
  logout(next = '/login', keepAlerts = true) {
    const vm = this;

    function logoutClientSide() {
      socketbridge.disconnect();

      // Preserve any visible alerts.
      const alerts = [];
      if (store.getters.hasVisibleAlerts && keepAlerts) {
        for (const alert of store.state.common.alerts) {
          alerts.push(alert);
        }
      }

      store.commit('RESET_ENTIRE_STATE');

      if (alerts) {
        for (const alert of alerts) {
          store.commit('ADD_ALERT', alert);
        }
      }

      router.push(next);
    }

    const promise = this.trackLogout();
    promise.then(() => logoutClientSide()).catch(() => logoutClientSide());

    return promise;
  }

  notifyAppWrapperCookiesHaveChanged() {
    if (
      window.MYSHOPRADIO_APP_WRAPPER !== undefined &&
      window.MYSHOPRADIO_APP_WRAPPER.onCookiesChanged !== undefined
    ) {
      window.MYSHOPRADIO_APP_WRAPPER.onCookiesChanged();
    }
  }

  trackLogout() {
    const promise = this.post('logout/track');
    return promise;
  }

  whoAmI() {
    return this.get('whoami');
  }

  /**
   * Return a list containing Decoders for either 'tv' or 'radio' content.
   *
   * ctype (str): 'radio' or 'tv'
   *
   * targetSpotId (Number): Optional. Limit results to those compatible
   *   with this Spot.
   *
   * includeMenuPlayers (Boolean): Optional, false by default. Also list
   *   players configured for displaying Menus.
   */
  listUserDecoders(ctype, targetSpotId = null, includeMenuPlayers = false) {
    const url = 'userdecoders/' + ctype;
    const params = {};
    if (targetSpotId) {
      // API has changed and we adapt to multiple 'target_contents' here.
      params.target_contents = JSON.stringify([
        {
          kind: ctype === 'radio' ? 'radio' : 'tvspotbundle',
          id: targetSpotId,
        },
      ]);
    }
    if (includeMenuPlayers) {
      params.include_menu_players = includeMenuPlayers;
    }
    return this.get(url, {params: params});
  }

  listUserDecodersMixed(mixedContents, includeMenuPlayers = false) {
    const url = 'userdecoders/mixed';
    const targetContents = mixedContents.map((content) => {
      return {
        id: content.id,
        kind: content.kind,
      };
    });
    const params = {target_contents: JSON.stringify(targetContents)};
    if (includeMenuPlayers) {
      params.include_menu_players = includeMenuPlayers;
    }
    return this.get(url, {params: params});
  }

  listUserTVDecodersForTicker(tickerId) {
    let url = 'userdecoders/ticker/' + tickerId;
    let promise = this.get(url);
    return promise;
  }

  listUserRadioDecodersForChannel(channelId) {
    let url = 'userdecoders/channel/' + channelId;
    let promise = this.get(url);
    return promise;
  }

  listTimeframes() {
    const url = 'usertimeframes';
    return this.get(url);
  }

  listTags() {
    const url = 'tags';
    return this.get(url);
  }

  listTaggableDecoders() {
    const url = 'tags/taggable_decoders';
    return this.get(url);
  }

  addTagToSpot(spotIdent, tagId) {
    const url = 'tags/spot/add';
    const payload = {
      spot_ident: spotIdent,
      tag_id: tagId,
      purge_existing_spotentries: true,
    };
    return this.post(url, payload);
  }

  removeTagFromSpot(spotIdent, tagId) {
    const url = 'tags/spot/remove';
    const payload = {
      spot_ident: spotIdent,
      tag_id: tagId,
    };
    return this.post(url, payload);
  }

  addTagToDecoder(decoderIdent, tagId) {
    const url = 'tags/decoder/add';
    const payload = {
      decoder_ident: decoderIdent,
      tag_id: tagId,
    };
    return this.post(url, payload);
  }

  removeTagFromDecoder(decoderIdent, tagId) {
    const url = 'tags/decoder/remove';
    const payload = {
      decoder_ident: decoderIdent,
      tag_id: tagId,
    };
    return this.post(url, payload);
  }

  listMenus() {
    const url = 'menus';
    return this.get(url);
  }

  setupMenuDemoForUserWorkspace() {
    const url = 'menus/demo/setup';
    return this.post(url);
  }

  retrieveMenu(menuId) {
    const url = 'menus/' + menuId;
    return this.get(url);
  }

  warnForMenuTVDecodersWithoutMenuCategories(menuId) {
    const url =
      'menus/' + menuId + '/warn_for_tvdecoders_without_menucategories';
    return this.get(url);
  }

  /**
   * Compute a menu pages preview payload for selected MenuCategories.
   *
   * As this is rather slow, we cancel the prior request to always only
   * get the latest results.
   */
  getMenuPreview(
    menuId,
    selectedMenuTemplateId = null,
    allowedMenuCategoryIds = null,
    cancelPriorRequest = true
  ) {
    if (cancelPriorRequest) {
      this.cancelCurrentRequest();
    }

    let url = 'menus/' + menuId + '/preview';

    const params = new URLSearchParams();
    if (selectedMenuTemplateId || allowedMenuCategoryIds) {
      if (selectedMenuTemplateId) {
        params.set('menu_template', selectedMenuTemplateId);
      }
      if (allowedMenuCategoryIds) {
        params.set('allowed_menu_categories', allowedMenuCategoryIds.join(','));
      }
    }

    return this.get(url, {params: params, allowCancellation: true});
  }

  getMenuItemHighlightPreviews(menuItemId) {
    const url = 'menuitems/' + menuItemId + '/highlight_previews';
    return this.get(url);
  }

  reorderMenuCategories(menuId, orderedMenuCategoryIds) {
    const url = 'menus/' + menuId + '/reorder_categories';
    const payload = {
      ordered_categories: orderedMenuCategoryIds
        .map((id) => String(id))
        .join(','),
    };
    return this.post(url, payload);
  }

  reorderMenuItems(menuCategoryId, orderedMenuItemIds) {
    const url = 'menucategories/' + menuCategoryId + '/reorder_menu_items';
    const payload = {
      ordered_menu_items: orderedMenuItemIds.map((id) => String(id)).join(','),
    };
    return this.post(url, payload);
  }

  listMenuCategories(menuId, tvdecoderId = null) {
    const url = 'menucategories';
    const params = {menu: menuId};
    if (tvdecoderId) {
      params.tvdecoder = tvdecoderId;
    }
    return this.get(url, {params: params});
  }

  setMenuCategoryContentModeOnTVDecoder(menuCategory, tvdecoder, contentMode) {
    const url = 'menucategories/tvdecoder/allowed/contentmode';
    const payload = {
      category_id: menuCategory.id,
      tvdecoder_id: tvdecoder.id,
      content_mode: contentMode,
    };
    return this.put(url, payload);
  }

  addMenuCategoryToTVDecoder(menuCategory, tvdecoder) {
    const url = 'menucategories/tvdecoder/allowed/add';
    const payload = {
      category_id: menuCategory.id,
      tvdecoder_id: tvdecoder.id,
    };
    return this.post(url, payload);
  }

  removeMenuCategoryFromTVDecoder(menuCategory, tvdecoder) {
    const url = 'menucategories/tvdecoder/allowed/remove';
    const payload = {
      category_id: menuCategory.id,
      tvdecoder_id: tvdecoder.id,
    };
    return this.post(url, payload);
  }

  listStoresWithTVDecodersForMenuCategories() {
    const url = 'menucategories/tvdecoders';
    return this.get(url);
  }

  addAllowedMenuCategoryToTVDecoder(menuCategoryId, tvdecoderId) {
    const url = 'menucategories/tvdecoder/allowed/add';
    const payload = {category_id: menuCategoryId, tvdecoder_id: tvdecoderId};
    return this.post(url, payload);
  }

  removeAllowedMenuCategoryFromTVDecoder(menuCategoryId, tvdecoderId) {
    const url = 'menucategories/tvdecoder/allowed/remove';
    const payload = {category_id: menuCategoryId, tvdecoder_id: tvdecoderId};
    return this.post(url, payload);
  }

  /**
   * Create a new MenuCategory.
   *
   * Args:
   *   menuId (Number): ID of parent Menu.
   *
   *   payload (Object):
   *     - name (String)
   *     - details (String)
   *     - timeframes (Array[Number])
   */
  createMenuCategory(menuId, payload) {
    const url = 'menucategories';
    return this.post(url, {
      menu: menuId,
      name: payload.name,
      details: payload.details,
      timeframes: payload.timeframes,
    });
  }

  /**
   *
   * Update an existing MenuCategory.
   *
   * Args:
   *   menuCategoryId (Number)
   *
   *   payload (Object):
   *     - name (String)
   *     - details (String)
   *     - timeframes (Array[Number])
   */
  updateMenuCategory(menuCategoryId, payload) {
    const url = 'menucategories/' + menuCategoryId;
    return this.patch(url, {
      name: payload.name,
      details: payload.details,
      timeframes: payload.timeframes,
    });
  }

  deleteMenuCategoryWithMenuItems(menuCategoryId) {
    const url = 'menucategories/' + menuCategoryId;
    return this.delete(url);
  }

  listMenuAdditives(menuId) {
    const url = 'menuadditives?menu=' + menuId;
    return this.get(url);
  }

  createMenuAdditive(menuId, name) {
    const url = 'menuadditives';
    const payload = {menu: menuId, name: name};
    return this.post(url, payload);
  }

  updateMenuAdditive(menuAdditiveId, payload) {
    const url = 'menuadditives/' + menuAdditiveId;
    return this.patch(url, payload);
  }

  deleteMenuAdditive(menuAdditiveId) {
    const url = 'menuadditives/' + menuAdditiveId;
    return this.delete(url);
  }

  listStoresForMenuItem(menuItemId) {
    const url = 'menuitems/' + menuItemId + '/stores';
    return this.get(url);
  }

  listMenuItems(menuId, storeId = null) {
    let url = 'menuitems?menu=' + menuId;
    if (storeId) {
      url += '&store=' + storeId;
    }
    return this.get(url);
  }

  createMenuItem(menuId, menuItem, duplicateOfMenuItemId = null) {
    // Since we are including an image upload we need FormData instead
    // of a JSON payload. Non-values must be empty strings, can't use
    // null, also no Date objects, use date strings instead.
    const formdata = new FormData();
    formdata.append('menu', menuId);
    formdata.append('beverage_deposit', menuItem.beverage_deposit || '');
    formdata.append('category', menuItem.category.id);
    formdata.append('description', (menuItem.description || '').trim());
    formdata.append('is_enabled', true);
    formdata.append('is_gluten_free', menuItem.is_gluten_free);
    formdata.append('is_highlight', false);
    formdata.append('is_lactose_free', menuItem.is_lactose_free);
    formdata.append('is_new', menuItem.is_new);
    formdata.append('is_vegan', menuItem.is_vegan);
    formdata.append('is_vegetarian', menuItem.is_vegetarian);
    formdata.append('name', menuItem.name);
    formdata.append(
      'hard_start',
      !menuItem.hard_start ? '' : menuItem.hard_start
    );
    formdata.append(
      'hard_expire',
      !menuItem.hard_expire ? '' : menuItem.hard_expire
    );

    // Drop 'extended' field values, if marked as 'not extended'.
    for (const variant of menuItem.variants) {
      if (!variant.extended) {
        variant.description = '';
        variant.additives = [];
      }
    }
    formdata.append('variants', JSON.stringify(menuItem.variants));

    for (const additive of menuItem.additives) {
      formdata.append('additives', additive.id);
    }

    if (!menuItem.image) {
      // No image chosen or removed? Inform backend to unset it.
      formdata.append('image', '');
    } else if (menuItem.image instanceof Blob) {
      formdata.append('image', menuItem.image, menuItem.image.name);
    } else if (typeof menuItem.image == 'string') {
      // Submit URL under different field name for the serializer to
      // handle, e.g. when duplicating an existing MenuItem with image.
      formdata.append('image_url', menuItem.image);
    }

    // Optional parameter that affects initial order value: We want
    // duplicates to be placed right below their original MenuItem.
    if (duplicateOfMenuItemId) {
      formdata.append('duplicate_of_menu_item_id', duplicateOfMenuItemId);
    }

    const url = 'menuitems';
    return this.post(url, formdata);
  }

  updateMenuItem(menuItemId, menuItem) {
    // Since we are including an image upload we need FormData instead
    // of a JSON payload. Non-values must be empty strings, can't use
    // null, also no Date objects, use date strings instead.
    const formdata = new FormData();
    formdata.append('beverage_deposit', menuItem.beverage_deposit || '');
    formdata.append('category', menuItem.category.id);
    formdata.append('description', (menuItem.description || '').trim());
    formdata.append('is_enabled', menuItem.is_enabled);
    formdata.append('is_gluten_free', menuItem.is_gluten_free);
    formdata.append('is_highlight', menuItem.is_highlight);
    formdata.append('is_lactose_free', menuItem.is_lactose_free);
    formdata.append('is_new', menuItem.is_new);
    formdata.append('is_vegan', menuItem.is_vegan);
    formdata.append('is_vegetarian', menuItem.is_vegetarian);
    formdata.append('name', menuItem.name);
    formdata.append(
      'hard_start',
      !menuItem.hard_start ? '' : new Date(menuItem.hard_start).toISOString()
    );
    formdata.append(
      'hard_expire',
      !menuItem.hard_expire ? '' : new Date(menuItem.hard_expire).toISOString()
    );

    // Drop 'extended' field values, if marked as 'not extended'.
    for (const variant of menuItem.variants) {
      if (!variant.extended) {
        variant.description = '';
        variant.additives = [];
      }
    }
    formdata.append('variants', JSON.stringify(menuItem.variants));

    for (const additive of menuItem.additives) {
      formdata.append('additives', additive.id);
    }

    if (!menuItem.image) {
      // No image chosen or removed? Inform backend to unset it.
      formdata.append('image', '');
    } else if (menuItem.image instanceof Blob) {
      formdata.append('image', menuItem.image, menuItem.image.name);
    }

    const url = 'menuitems/' + menuItemId;
    return this.patch(url, formdata);
  }

  retrieveMenuItem(menuItemId) {
    const url = 'menuitems/' + menuItemId;
    return this.get(url);
  }

  deleteMenuItem(menuItemId) {
    const url = 'menuitems/' + menuItemId;
    return this.delete(url);
  }

  activateMenuItemOnStores(menuItemId, storeIds) {
    const url = 'menuitemdeactivations/stores/activate';
    const payload = {
      menu_item: menuItemId,
      store_ids: storeIds.join(','),
    };
    return this.post(url, payload);
  }

  deactivateMenuItemOnStores(menuItemId, storeIds, until = null) {
    const url = 'menuitemdeactivations/stores/deactivate';
    const payload = {
      menu_item: menuItemId,
      store_ids: storeIds.join(','),
      until: until,
    };
    return this.post(url, payload);
  }

  updateTimeframe(timeframe) {
    const url = 'usertimeframes/' + timeframe.id;
    const payload = {
      name: timeframe.name,
      start: timeframe.start,
      end: timeframe.end,
      weekdays: timeframe.weekdays,
      group_id: timeframe.group.id,
    };
    return this.patch(url, payload);
  }

  updateTag(tag) {
    const url = 'tags/' + tag.id;
    const payload = {
      group: tag.group,
      name: tag.name,
    };
    if (tag.color) {
      payload.color = tag.color;
    }

    // Swap tag in frontend immediately so we don't see the outdated
    // version until the response comes in, feels better.
    store.commit('UPDATE_TAG_IN_LIST', tag);

    return this.patch(url, payload);
  }

  deleteTimeframe(timeframeId) {
    const url = 'usertimeframes/' + timeframeId;
    return this.delete(url);
  }

  deleteTag(tagId) {
    const url = 'tags/' + tagId;
    return this.delete(url);
  }

  getTimeframeRelatedContentDescriptions(timeframeId) {
    const url = 'usertimeframes/' + timeframeId + '/relatedcontents';
    return this.get(url);
  }

  createTimeframe(timeframe) {
    const url = 'usertimeframes';
    const payload = {
      name: timeframe.name,
      start: timeframe.start,
      end: timeframe.end,
      weekdays: timeframe.weekdays,
      group_id: timeframe.group.id,
    };
    return this.post(url, payload);
  }

  createTag(tag) {
    const url = 'tags';
    const payload = {
      name: tag.name,
      group: tag.group,
    };
    if (tag.color) {
      payload.color = tag.color;
    }
    return this.post(url, payload);
  }

  /**
   * Fetch a page of 'Sites'.
   *
   * Page size is passed as a query param. We can optionally filter by
   * some text, uploadgroups or tags. Some expensive decoder fields will
   * be omitted which are not needed for the sites page, e.g.
   * current_menu.
   *
   * Earlier unfinished requests with the same cancellation token are
   * cancelled.
   */
  listSites(opts) {
    const url = 'sites';
    const params = {};
    if (opts.radio) {
      params.radio = opts.radio;
    }
    if (opts.tv) {
      params.tv = opts.tv;
    }
    if (opts.page) {
      params.page = opts.page;
    }
    if (opts.searchText) {
      params.search = opts.searchText;
    }
    if (opts.groupIds) {
      params.groups = opts.groupIds.join(',');
    }
    if (opts.tagIds) {
      params.tags = opts.tagIds.join(',');
    }
    return this.get(url, {
      params: params,
      allowCancellation: true,
    });
  }

  /**
   * Note: This will include TVDecoder.current_menu.
   */
  retrieveSite(storeId) {
    const url = 'sites';
    return this.get(url, {
      params: {store_id: storeId},
      allowCancellation: true,
    }).then((response) => {
      return response.data.results.sites[0];
    });
  }

  listUserWritableStores() {
    const url = 'stores/writable';
    return this.get(url);
  }

  listStoresForOpeningHours() {
    const url = 'stores/openinghours';
    return this.get(url);
  }

  /**
   * Set the opening hours on all given Stores.
   *
   * User needs to have writeaccess on each Store to update them.
   *
   * The payload is expected to contain all weekday opening hours fields
   * like:
   *   opening_hours_monday_start
   *   opening_hours_monday_end
   *   etc.
   */
  batchUpdateStoreOpeningHours(storeIds, payload) {
    const url = 'stores/openinghours';
    payload.store_ids = storeIds.join(',');
    return API.patch(url, payload);
  }

  listCampaignsForUser(opts) {
    const url = 'campaigns';
    const params = {
      page: opts.pageIndex,
    };
    if (opts.searchText) {
      params.search = opts.searchText;
    }
    return this.get(url, {params: params});
  }

  listContentForNewCampaign(groupId, opts) {
    const url = 'campaigns/' + groupId + '/content';
    const params = {};
    if (opts.search) {
      params.search = opts.search;
    }
    if (opts.excludedContentIdents) {
      params.exclude = opts.excludedContentIdents.join(',');
    }
    return this.get(url, {params: params});
  }

  /**
   * Return whether given title is unique for Campaigns in Group.
   */
  checkCampaignTitleUniqueWithinGroup(groupId, title) {
    const url = 'campaigns/' + groupId + '/new/title';
    const params = {
      title: title,
    };
    return this.get(url, {params: params});
  }

  /**
   * Create new Campaign and return its ID.
   *
   * opts (Object):
   *   - title (String)
   *   - contentIdents (Array[String])
   *   - poster (File): Optional poster image.
   *   - defaultStartDate (String): Optional.
   *   - defaultStopDate (String): Optional.
   */
  createNewCampaign(groupId, opts) {
    const url = 'campaigns/' + groupId + '/new';

    const formdata = new FormData();
    formdata.append('title', opts.title);
    formdata.append('content_idents', opts.contentIdents.join(','));

    if (opts.poster) {
      formdata.append('poster', opts.poster, opts.poster.name);
    }
    if (opts.defaultStartDate) {
      formdata.append('default_startdate', opts.defaultStartDate);
    }
    if (opts.defaultStopDate) {
      formdata.append('default_stopdate', opts.defaultStopDate);
    }

    return this.post(url, formdata);
  }

  /**
   * Return matching TVTemplates page for user and query params.
   *
   * opts (Object):
   *   - pageIndex (Number): Optional, 1 if not specified
   *   - branchId (Number): Optional
   *   - searchText (String): Optional
   *   - landscape (Boolean): Optional
   *   - portrait (Boolean): Optional
   *   - favorites (Boolean): Optional
   */
  listTVTemplatesForUser(opts) {
    if (!opts) {
      opts = {};
    }

    let url = 'tvtemplates?page=' + (opts.pageIndex || 1);
    if (opts.branchId) {
      url += '&branch=' + opts.branchId;
    }
    if (opts.searchText) {
      url += '&search=' + encodeURIComponent(opts.searchText);
    }
    if (opts.landscape && !opts.portrait) {
      url += '&landscape=1';
    }
    if (opts.portrait && !opts.landscape) {
      url += '&portrait=1';
    }
    if (opts.favorites) {
      url += '&favorites=1';
    }
    return this.get(url);
  }

  listDevices() {
    const url = 'devices';
    return this.get(url);
  }

  /**
   * Upload an image file to be stored for use in a TVTemplate.
   *
   * Args:
   *
   *   templateId (Number): ID of the TVTemplate this is uploaded for
   *
   *   file (File): The file content to upload
   *
   *   originalFilename (String): The name under which to save the file
   *
   *   opts (Object):
   *     - scaleWidth (Number): Optional, helps to ensure image fits container
   *     - scaleHeight (Number): Optional, helps to ensure image fits container
   *     - spritesheetWidth (Number): Optional, used for Adobe Animate compatibility
   *     - spritesheetHeight (Number): Optional, used for Adobe Animate compatibility
   *
   * Returns :
   *
   *   Promise
   */
  uploadCroppedImageForUseInTVTemplate(
    templateId,
    file,
    originalFilename,
    opts = null
  ) {
    opts = opts || {};

    const url = `tvtemplates/${templateId}/image/upload`;
    const formdata = new FormData();
    // Note: Passing the filename separately ensures that it is
    //   available in the backend even when 'file' is actually a Blob
    //   object e.g. due to Edge compatibility workarounds.
    //   See: https://stackoverflow.com/a/22624552
    formdata.append('file', file, originalFilename);

    if (opts.scaleWidth && opts.scaleHeight) {
      formdata.append('scale_width', opts.scaleWidth);
      formdata.append('scale_height', opts.scaleHeight);
    }

    if (opts.spritesheetWidth && opts.spritesheetHeight) {
      formdata.append('spritesheet_width', opts.spritesheetWidth);
      formdata.append('spritesheet_height', opts.spritesheetHeight);
    }

    return this.post(url, formdata);
  }

  convertTifToPng(tifFile, originalFilename) {
    const url = `tvtemplates/tif2png`;
    const formdata = new FormData();
    // Note: Passing the filename separately ensures that it is
    //   available in the backend even when 'file' is actually a Blob
    //   object e.g. due to Edge compatibility workarounds.
    //   See: https://stackoverflow.com/a/22624552
    formdata.append('file', tifFile, originalFilename);

    return this.post(url, formdata);
  }

  /**
   * Upload an image file to be stored for use in an EditableTVSpotTemplate.
   *
   * Args:
   *
   *   templateId (Number): ID of the EditableTVSpotTemplate this is uploaded for
   *
   *   file (File): The file content to upload
   *
   *   originalFilename (String): The name under which to save the file
   *
   *   opts (Object):
   *     - scaleWidth (Number): Optional, helps to ensure image fits container
   *     - scaleHeight (Number): Optional, helps to ensure image fits container
   *     - spritesheetWidth (Number): Optional, used for Adobe Animate compatibility
   *     - spritesheetHeight (Number): Optional, used for Adobe Animate compatibility
   *
   * Returns :
   *
   *   Promise
   */
  uploadCroppedImageForUseInEditableTVSpotTemplate(
    templateId,
    file,
    originalFilename,
    opts = null
  ) {
    opts = opts || {};

    const url = `editabletvspottemplates/${templateId}/image/upload`;
    const formdata = new FormData();

    // Note: Passing the filename separately ensures that it is
    //   available in the backend even when 'file' is actually a Blob
    //   object e.g. due to Edge compatibility workarounds.
    //   See: https://stackoverflow.com/a/22624552
    formdata.append('file', file, originalFilename);

    if (opts.scaleWidth && opts.scaleHeight) {
      formdata.append('scale_width', opts.scaleWidth);
      formdata.append('scale_height', opts.scaleHeight);
    }

    if (opts.spritesheetWidth && opts.spritesheetHeight) {
      formdata.append('spritesheet_width', opts.spritesheetWidth);
      formdata.append('spritesheet_height', opts.spritesheetHeight);
    }

    return this.post(url, formdata);
  }

  produceTvspotFromTemplateData({
    category,
    hardExpire,
    hardStart,
    produceAsPublished,
    selectedUploadGroupId,
    tags,
    templateData,
    templateId,
    title,
  }) {
    const url = `tvtemplates/${templateId}/produce`;

    const tagIds = tags.map((tag) => tag.id);
    const payload = {
      category: category.id,
      group_id: selectedUploadGroupId,
      hard_expire: hardExpire,
      hard_start: hardStart,
      published: produceAsPublished,
      tags: tagIds,
      template_data: templateData,
      title: title,
    };
    return this.post(url, payload);
  }

  updateTvspotWithTemplateData(tvspotId, templateData) {
    const url = `tvtemplates/spot/${tvspotId}`;
    const payload = {
      template_data: templateData,
    };
    return this.patch(url, payload);
  }

  listBranches() {
    const url = 'branches';
    return this.get(url);
  }

  listBrandings() {
    const url = 'brandings';
    return this.get(url);
  }

  retrieveBrandingBackgroundBranchImageUrl(branchId) {
    const url = 'brandings/backgroundbranchimage?branch_id=' + branchId;
    return this.get(url);
  }

  _getBrandingFormDataFromPayload(payload) {
    function _hex8(hexString) {
      return tinycolor(hexString).toHex8String();
    }

    const formdata = new FormData();
    if (payload.branch_id) {
      formdata.append('branch_id', payload.branch_id);
    }
    formdata.append('buttonwidget_boxtype', payload.buttonwidget_boxtype);

    formdata.append(
      'buttonwidget_color_background',
      _hex8(payload.buttonwidget_color_background)
    );
    formdata.append(
      'buttonwidget_color_text',
      _hex8(payload.buttonwidget_color_text)
    );
    formdata.append('color_background', _hex8(payload.color_background));
    formdata.append('color_headline', _hex8(payload.color_headline));
    formdata.append('color_primary', _hex8(payload.color_primary));
    formdata.append('color_secondary', _hex8(payload.color_secondary));

    formdata.append('font_name', payload.font_name);
    formdata.append('is_default', payload.is_default);
    formdata.append('name', payload.name);
    formdata.append('pricewidget_boxtype', payload.pricewidget_boxtype);

    formdata.append(
      'pricewidget_color_background',
      _hex8(payload.pricewidget_color_background)
    );

    formdata.append(
      'pricewidget_color_text',
      _hex8(payload.pricewidget_color_text)
    );
    formdata.append(
      'pricewidget_rebate_color_background',
      _hex8(payload.pricewidget_rebate_color_background)
    );
    formdata.append(
      'pricewidget_rebate_color_text',
      _hex8(payload.pricewidget_rebate_color_text)
    );

    if (payload.logo instanceof Blob) {
      // Pass a cropped image as Blob to stored to disk.
      formdata.append('logo', payload.logo, payload.logo.name);
    } else if (typeof payload.logo === 'string') {
      // We already have a URL, pass it under a different field name.
      // For creation, this happens when a Branding is copied.
      formdata.append('logo_url', payload.logo);
    }

    return formdata;
  }

  createBranding(payload) {
    const url = 'brandings';
    const formdata = this._getBrandingFormDataFromPayload(payload);
    return this.post(url, formdata);
  }

  updateBranding(brandingId, payload) {
    const url = 'brandings/' + brandingId;
    const formdata = this._getBrandingFormDataFromPayload(payload);
    return this.patch(url, formdata);
  }

  deleteBranding(brandingId) {
    const url = 'brandings/' + brandingId;
    return this.delete(url);
  }

  retrieveContentCreatorCanDoTestBooking() {
    const url = 'contentcreator/booking/test';
    return this.get(url);
  }

  listContentCreatorPlans() {
    const url = 'contentcreator/plans';
    return this.get(url);
  }

  contentCreatorWhatIsBookable() {
    const url = 'contentcreator/bookable';
    return this.get(url);
  }

  listContentCreatorBookingRequests() {
    const url = 'contentcreator/bookingrequests';
    return this.get(url);
  }

  listContentCreatorPlanBookings() {
    const url = 'contentcreator/planbookings';
    return this.get(url);
  }

  bookContentCreatorPlan(planId, workspaceId, isTest, billingPeriod) {
    const url = 'contentcreator/plans/' + planId + '/book';
    return this.post(url, {
      billing_period: billingPeriod,
      create_demo_branding: isTest,
      important: true,
      is_test: isTest,
      workspace_id: workspaceId,
    });
  }

  requestBookContentCreatorPlan(planId, workspaceId, billingPeriod) {
    const url = 'contentcreator/plans/' + planId + '/book/request';
    return this.post(url, {
      workspace_id: workspaceId,
      billing_period: billingPeriod,
    });
  }

  cancelContentCreatorPlanBooking(bookingId) {
    const url = 'contentcreator/planbookings/' + bookingId;
    return this.delete(url, {important: true});
  }

  retrieveUserWorkspacePage() {
    const url = 'userworkspace';
    return this.get(url);
  }

  listRadioChannels() {
    const url = 'channels';
    return this.get(url);
  }

  retrieveRadioChannel(channelId) {
    const url = 'channels/' + channelId;
    return this.get(url);
  }

  retrieveGlobalChannelConfig() {
    const url = 'channels/globalconfig';
    return this.get(url);
  }

  activateChannelOnSingleRadioDecoder(channelId, radioDecoderId) {
    let url = `radiodecoders/${radioDecoderId}/channel/${channelId}`;
    let promise = this.patch(url);
    return promise;
  }

  activateChannelOnMultipleRadioDecoders(channelId, radioDecoderIds) {
    const url = `channels/${channelId}/batch`;
    const payload = {
      decoder_ids: radioDecoderIds.join(','),
    };
    const promise = this.patch(url, payload);
    return promise;
  }

  /**
   * Get a <TV|Radio>Decoder from the backend.
   *
   * ctype (String): Either 'radio' or 'tv'.
   * id (Number): Decoder id.
   */
  retrieveDecoder(ctype, id) {
    const url = ctype + 'decoders/' + id;
    return API.get(url, {allowCancellation: true});
  }

  updateStoreAssociationMemberNumber(storeId, associationMemberNumber) {
    const url = `stores/${storeId}/membernumber`;
    const payload = {
      association_membernumber: associationMemberNumber,
    };
    return API.patch(url, payload);
  }

  setDecoderIgnoreTagging(decoderIdent, ignore) {
    const url = 'tags/decoder/ignore';
    const payload = {
      decoder_ident: decoderIdent,
      ignore: ignore,
    };
    return API.post(url, payload);
  }

  getStoreDecoderDescriptions(storeId) {
    const url = `stores/${storeId}/decoderdescriptions`;
    const promise = API.get(url);
    return promise;
  }

  getSpotStoreStatistics(ctype, spotId) {
    const url = `stats/store/content/${ctype}/${spotId}`;
    return API.get(url);
  }

  submitGroupsContentsStoreStatisticsRequest(
    ctypes,
    selectedGroupIds,
    startDate = null,
    stopDate = null
  ) {
    const url = `stats/store/content/groups`;
    const payload = {
      groups: selectedGroupIds.join(','),
      ctypes: ctypes.join(','),
    };
    if (startDate) {
      payload.startdate = startDate;
    }
    if (stopDate) {
      payload.stopdate = stopDate;
    }
    return API.post(url, payload);
  }

  renameDecoder(ctype, id, description) {
    const url = `${ctype}decoders/${id}`;
    const payload = {
      description: description,
    };
    return API.patch(url, payload);
  }

  updateTouchDecoderWebpage(id, webpage) {
    const url = `touchdecoders/${id}`;
    const payload = {
      webpage: webpage,
    };
    return API.patch(url, payload);
  }

  /**
   * Activate contents on Decoders.
   *
   * Args:
   *
   *   ctype (String): One of 'radio', 'tv'.
   *
   *   decoderIds (Array[Number]): TVDecoder IDs.
   *
   *   spotIds (Array[Number]): IDs of RadioSpots or TVSpotBundles
   *
   *   opts (Object): Optional attributes like:
   *     - closingtime_minutes_before_closing (Number)
   *     - hotrotation (Boolean)
   *     - startdate (String)
   *     - stopdate (String)
   *     - weekdays (Array[Number])
   *     - timeframeIds (Array[Number])
   *     - spotIdToOverlayData (Object)
   */
  createSpotEntries(ctype, decoderIds, spotIds, opts) {
    const url = 'spotentries/' + ctype;
    const payload = {
      closingtime_minutes_before_closing:
        opts.closingtime_minutes_before_closing,
      decoder_ids: decoderIds.join(','),
      hotrotation: opts.hotrotation,
      spot_ids: spotIds.join(','),
      startdate: opts.startdate,
      stopdate: opts.stopdate,
    };
    if (opts.weekdays) {
      payload.weekdays = opts.weekdays.join(',');
    }
    if (opts.timeframeIds && opts.timeframeIds.length > 0) {
      payload.timeframe_ids = opts.timeframeIds.join(',');
    }
    if (opts.spotIdToOverlayData) {
      payload.spot_id_to_overlay_data = JSON.stringify(
        opts.spotIdToOverlayData
      );
    }
    return API.post(url, payload);
  }

  addTickerToTVDecoder(decoderId, tickerId, opts) {
    let url = 'usertickers/' + decoderId + '/' + tickerId;
    const payload = {
      startdate: opts.startdate,
      stopdate: opts.stopdate,
    };
    return API.post(url, payload);
  }

  retrieveSpotEntry(type, id) {
    let url = type + 'spotentries/' + id;
    let promise = API.get(url);
    return promise;
  }

  /**
   * List SpotEntry/TickerEntry objects filtered by given ids.
   */
  listEntriesByIds(ctype, ids) {
    // Results in: 'radiospotentries' or 'tvspotentries' or 'tickerentries'.
    let prefix = ctype === 'radio' || ctype === 'tv' ? 'spot' : '';
    let url = ctype + prefix + 'entries';

    let params = {ids: ids};
    let promise = API.get(url, {params: params});
    return promise;
  }

  /**
   * Retrieve a SpotEntry where we don't know the id yet.
   */
  retrieveUnknownSpotEntry(type, decoderId, spotId) {
    let url = 'spotentries/' + type;
    let params = {
      decoder_id: decoderId,
      spot_id: spotId,
    };
    let promise = API.get(url, {params: params});
    return promise;
  }

  /**
   * Return RadioSpot/TVSpotBundle as Promise and update contents list.
   */
  retrieveContent(ctype, id) {
    let url;
    if (ctype === 'tv') {
      url = 'tvspotbundles/' + id;
    } else {
      url = 'radiospots/' + id;
    }

    const promise = API.get(url);
    promise.then((response) => {
      const spot = response.data;
      store.commit('UPDATE_CONTENT_IN_LISTS', spot);
    });

    return promise;
  }

  getDownloadUrlForTVSpot(id) {
    const url = 'content/tv/' + id + '/download/';
    return API.get(url);
  }

  getTVSpotOverlayOptions(tvspotId) {
    const url = 'overlayoptions/tvspot/' + tvspotId;
    const tokenAuth = false;
    return API.request('GET', url, null, null, tokenAuth);
  }

  getTVTemplateOverlayOptions(tvtemplateId) {
    const url = 'overlayoptions/tvtemplate/' + tvtemplateId;
    const tokenAuth = false;
    return API.request('GET', url, null, null, tokenAuth);
  }

  retrieveCampaignDetail(id) {
    const url = 'campaigns/' + id;
    return API.get(url);
  }

  retrieveTVTemplateDetail(tvtemplateId, branchId = null) {
    let url = 'tvtemplates/' + tvtemplateId;
    if (branchId) {
      url += '?branch=' + branchId;
    }
    return API.get(url);
  }

  updateTVTemplateFavorited(tvtemplateId, favorited) {
    const url = 'tvtemplates/' + tvtemplateId + '/favorite';
    return API.patch(url, {favorited: favorited});
  }

  activateCampaign(campaignId, payload) {
    const url = `campaigns/${campaignId}/activate`;
    return API.post(url, payload);
  }

  deactivateCampaign(campaignId) {
    const url = `campaigns/${campaignId}/deactivate`;
    return API.post(url);
  }

  /**
   * Activate mixed contents on mixed decoders.
   *
   * Args:
   *   contents (Array): Mixed list of TVSpotBundle|RadioSpot|Ticker
   *
   *   decoders (Array): Mixed list of TVDecoder|RadioDecoder
   *
   *   opts (Object):
   *     - closingtime_minutes_before_closing (Number)
   *     - hotrotation (null|Boolean): Optional
   *     - overlayData (null|Object): Optional
   *     - startDate (null|String): Optional startdate string
   *     - stopDate (null|String): Optional stopdate string
   *     - weekdays (Array[Number])
   *     - timeframeIds (null|Array[Number]): Optional
   *
   * Returns:
   *   Promise:
   *     {'warnings': [...]}
   */
  activateSelectedContents(contents, decoders, opts) {
    const payload = {
      contents: JSON.stringify(contents.map((content) => content.ident)),
      decoders: JSON.stringify(decoders.map((decoder) => decoder.ident)),
    };
    if (opts.startDate) {
      payload.startdate = opts.startDate;
    }
    if (opts.stopDate) {
      payload.stopdate = opts.stopDate;
    }
    if (opts.weekdays) {
      payload.weekdays = opts.weekdays.join(',');
    }
    if (opts.closingtime_minutes_before_closing) {
      payload.closingtime_minutes_before_closing =
        opts.closingtime_minutes_before_closing;
    }
    if (opts.hotrotation) {
      payload.hotrotation = opts.hotrotation;
    }
    if (opts.timeframeIds && opts.timeframeIds.length > 0) {
      payload.timeframe_ids = opts.timeframeIds.join(',');
    }
    if (opts.spotIdToOverlayData) {
      payload.spot_id_to_overlay_data = JSON.stringify(
        opts.spotIdToOverlayData
      );
    }
    const url = 'contentselection/activate';
    return API.post(url, payload);
  }

  /**
   * Deactivate mixed contents on all of User's decoders.
   *
   * Args:
   *   contents (Array): Mixed list of TVSpotBundle|RadioSpot|Ticker
   *
   * Can also deactivate tagged contents (aka flagging TagEntry as
   * active=false), when the content allows it.
   *
   * Returns:
   *   Promise:
   *     {'warnings': [...]}
   */
  deactivateSelectedContents(contents) {
    const payload = {
      contents: JSON.stringify(contents.map((content) => content.ident)),
    };
    const url = 'contentselection/deactivate';
    return API.post(url, payload);
  }

  /**
   * Delete mixed contents entirely (deactivates them as well).
   *
   * Args:
   *   contents (Array): Mixed list of TVSpotBundle|RadioSpot|Ticker
   *
   * Returns:
   *   Promise:
   *     {'warnings': [...]}
   */
  deleteSelectedContents(contents) {
    const payload = {
      contents: JSON.stringify(contents.map((content) => content.ident)),
    };
    const url = 'contentselection/delete';
    return API.post(url, payload);
  }

  /**
   * Bundle selected TV contents if possible.
   *
   * Args:
   *   contents (Array): Mixed list of TVSpotBundle|RadioSpot|Ticker
   *
   * Returns:
   *   Promise:
   *     {'warnings': [...]}
   */
  bundleSelectedContents(contents) {
    const payload = {
      contents: JSON.stringify(contents.map((content) => content.ident)),
    };
    const url = 'contentselection/bundle';
    return API.post(url, payload);
  }

  /**
   * Set date of selected contents if possible, changing contents order.
   *
   * Args:
   *   contents (Array): Mixed list of TVSpotBundle|RadioSpot|Ticker
   *
   * Returns:
   *   Promise:
   *     {'warnings': [...]}
   */
  refreshDateSelectedContents(contents) {
    const payload = {
      contents: JSON.stringify(contents.map((content) => content.ident)),
    };
    const url = 'contentselection/refreshdate';
    return API.post(url, payload);
  }

  /**
   * Delete a Radio- or TVSpot (and associated UploadImage).
   *
   * User must be contentmanager of Group for this.
   */
  removeSpot(ctype, spotId) {
    const url = `removespot/${ctype}/${spotId}`;
    const promise = API.delete(url);
    return promise;
  }

  /**
   * Return Ticker as Promise and make sure to update the contents list.
   */
  retrieveUserTicker(id) {
    const url = 'usertickers/' + id;
    const promise = API.get(url);

    promise.then((response) => {
      const ticker = response.data;
      store.commit('UPDATE_CONTENT_IN_LISTS', ticker);
    });

    return promise;
  }

  renameContent(ctype, spotId, title) {
    const url = 'renamecontent';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
      title: title,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  changeImageDuration(tvspotId, duration) {
    const url = 'changeimageduration';
    const payload = {
      tvspot_id: tvspotId,
      duration: duration,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  refreshContentDate(ctype, spotId) {
    const url = 'refreshcontentdate';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  updateContentHardRange(ctype, spotId, newHardStart, newHardExpire) {
    const url = 'setcontenthardrange';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
      hard_start: newHardStart,
      hard_expire: newHardExpire,
    };
    return API.patch(url, payload);
  }

  changeContentCategory(ctype, spotId, categoryId) {
    const url = 'categorizecontent';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
      category_id: categoryId,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  disbandTVSpotBundle(bundleId) {
    const url = 'disbandbundle';
    const payload = {
      bundle_id: bundleId,
    };
    return API.post(url, payload);
  }

  toggleTagEntriesCanBeDeactivated(spotIdent) {
    const url = 'toggle-tag-entries-can-be-deactivated';
    const payload = {
      spot_ident: spotIdent,
    };
    return API.post(url, payload);
  }

  publishContent(ctype, spotId) {
    const url = 'publishcontent';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  publishCampaign(campaignId) {
    const url = 'publishcampaign';
    const payload = {
      campaign_id: campaignId,
    };
    const promise = API.patch(url, payload);
    return promise;
  }

  unpublishContent(ctype, spotId) {
    const url = 'unpublishcontent';
    const payload = {
      ctype: ctype,
      spot_id: spotId,
    };
    const promise = API.post(url, payload);
    return promise;
  }

  unpublishCampaign(campaignId, contentsAlso) {
    const url = 'unpublishcampaign';
    const payload = {
      campaign_id: campaignId,
    };
    if (contentsAlso) {
      payload['contents_also'] = true;
    }
    const promise = API.patch(url, payload);
    return promise;
  }

  deleteCampaign(campaignId, contentsAlso) {
    const url = 'deletecampaign';
    const payload = {
      campaign_id: campaignId,
    };
    if (contentsAlso) {
      payload['contents_also'] = true;
    }
    const promise = API.post(url, payload);
    return promise;
  }

  /**
   * Update .play_after_entry attribute with given SpotEntry.
   *
   * Args:
   *   ident (String): Ident of SpotEntry e.g. 'tvspotentry_123'.
   *   playAfterEntryId (Number): ID of SpotEntry to link against.
   *
   * SpotEntries that are linked together must be of same ctype.
   */
  linkAgainstSpotEntry(entryIdent, playAfterEntryId) {
    const payload = {
      play_after_entry_id: playAfterEntryId,
    };
    return API.batchUpdateEntries([entryIdent], payload);
  }

  /**
   * Udate multiple Entries in one request.
   *
   * ctype can be one of: 'radio', 'closingtime', 'tv', 'ticker'
   *
   * Entry IDs can be from:
   * - RadioSpotEntry
   * - ClosingTimeRadioSpotEntry
   * - TVSpotEntry
   * - TickerEntry
   *
   * All entries get assigned a shared data payload, the endpoint needs
   * to handle this as needed.
   */
  batchUpdateEntries(entryIdents, payload) {
    const url = 'spotentries/mixed/batch';
    payload.entry_idents = entryIdents.join(',');
    const promise = API.patch(url, payload);
    return promise;
  }

  /**
   * Delete a SpotEntry depending on ctype:
   *
   * - closingtime: ClosingTimeRadioSpotEntry
   * - radio: RadioSpotEntry
   * - ticker: TickerEntry
   * - tv: TVSpotEntry
   */
  deleteSpotEntry(ctype, id) {
    let url = ctype + 'spotentries/' + id;
    let promise = API.delete(url);
    return promise;
  }

  /**
   * De-/activate a TagEntry depending on ctype:
   *
   * - radio: RadioTagEntry
   * - tv: TVTagEntry
   */
  toggleActivateTagEntry(ctype, id) {
    const url = 'tagentries/' + ctype + '/' + id;
    const promise = API.post(url);
    return promise;
  }

  /**
   * Delete given Entry objects.
   *
   * Entries are identified via their .ident and can be of e.g.:
   * - ClosingTimeRadioSpotEntry
   * - RadioSpotEntry
   * - TickerEntry
   * - TVSpotEntry
   */
  deleteSpotEntries(idents) {
    const url = 'spotentries/mixed/batch/delete';
    const payload = {
      entry_idents: idents.join(','),
    };
    // Can't use our delete() convenience function as it does not
    //   support passing a payload.
    return API.request('delete', url, payload, null);
  }

  /**
   * Reactivate given TagEntry objects.
   *
   * If an entry is active=true already, just skip it.
   *
   * Entries are identified via their .ident and can be of e.g.:
   * - RadioTagEntry
   * - TVTagEntry
   */
  reactivateTagEntries(tagEntryIdents) {
    const url = 'tagentries/batch/reactivate';
    const payload = {
      entry_idents: tagEntryIdents.join(','),
    };
    return API.post(url, payload);
  }

  getOverlayDataForHtmlPreview(spot) {
    const url = 'overlaydata/' + spot.id + '/preview';
    return API.get(url);
  }

  updateManagedUser(managedUserId, payload) {
    const url = `management/users/${managedUserId}`;
    return API.patch(url, payload);
  }

  editUserNames(userId, firstName, lastName) {
    const url = `management/users/${userId}/rename`;
    const payload = {};
    if (firstName) {
      payload.first_name = firstName;
    }
    if (firstName) {
      payload.last_name = lastName;
    }
    return API.patch(url, payload);
  }

  removeTwofactorAuthForManagedUser(userId) {
    const url = `management/users/${userId}/remove2fa`;
    return API.delete(url);
  }

  updateIsManagedCoadmin(userId, isManagedCoadmin) {
    const url = `management/users/${userId}/coadmin`;
    const payload = {is_managed_coadmin: isManagedCoadmin};
    return API.patch(url, payload);
  }

  deleteManagedUser(managedUserId) {
    const url = `management/users/${managedUserId}`;
    const promise = API.delete(url);
    return promise;
  }

  createManagedUserGroupAccess(
    userId,
    groupId,
    role,
    level,
    canManageContent,
    canAccessVisitorcount
  ) {
    const url = `management/users/${userId}/groupaccess`;
    const payload = {
      group_id: groupId,
      role: role,
      level: level,
      can_manage_content: !!canManageContent,
      can_access_visitorcount: !!canAccessVisitorcount,
    };
    return API.post(url, payload);
  }

  updateManagedUserGroupAccess(userId, groupAccessId, groupaccess) {
    const url = `management/users/${userId}/groupaccess/${groupAccessId}`;
    const payload = {
      level: groupaccess.level,
      role: groupaccess.role,
      can_manage_content: groupaccess.can_manage_content,
      can_access_visitorcount: groupaccess.can_access_visitorcount,
    };
    return API.patch(url, payload);
  }

  deleteManagedUserGroupAccess(userId, groupAccessId) {
    const url = `management/users/${userId}/groupaccess/${groupAccessId}`;
    return API.delete(url);
  }

  createManagedUserStoreAccess(userId, storeId, level) {
    const url = `management/users/${userId}/storeaccess`;
    const payload = {
      store_id: storeId,
      level: level,
    };
    return API.post(url, payload);
  }

  updateManagedUserStoreAccess(userId, storeAccessId, storeaccess) {
    const url = `management/users/${userId}/storeaccess/${storeAccessId}`;
    const payload = {
      level: storeaccess.level,
      can_access_visitorcount: storeaccess.can_access_visitorcount,
    };
    return API.patch(url, payload);
  }

  deleteManagedUserStoreAccess(userId, storeAccessId) {
    const url = `management/users/${userId}/storeaccess/${storeAccessId}`;
    return API.delete(url);
  }

  deleteTickerFromDecoder(tickerId, decoderId) {
    let url = 'usertickers/' + decoderId + '/' + tickerId;
    let promise = API.delete(url);
    return promise;
  }

  deleteTicker(tickerId) {
    let url = 'usertickers/' + tickerId;
    let promise = API.delete(url);
    return promise;
  }

  updateTicker(tickerId, payload) {
    let url = 'usertickers/' + tickerId;
    let promise = API.patch(url, payload);
    return promise;
  }

  createTicker(message, groupId) {
    let url = 'usertickers';
    let payload = {
      message: message,
      group: groupId,
    };
    let promise = API.post(url, payload);
    return promise;
  }

  /**
   * Upload an image file to the server.
   *
   * Args:
   *
   *   title (String): The title to use for the new TVSpot.
   *
   *   uploadGroupId (Number): The Group id to associate with.
   *
   *   categoryId (Number): The SpotCategory id to associate with.
   *
   *   hardStart (String): Start date of hard-range, can be null.
   *   hardExpire (String): End date of hard-range, can be null.
   *
   *   file (File): The file to upload.
   *
   *   reuploadSpotId (Number): Optional. Spot id to replace this with.
   *     Note: User is allowed to replace the Spot when its Group is one
   *     of his uploadgroups.
   *
   *   duration (Number): Duration in seconds to use for the video.
   *
   *   tags (Array[object]): Optional list of Tags to assign to the new
   *     content. We'll only submit their IDs in here.
   *
   *   bundleMainTvSpotId (Number): Optional TVSpot ID to use as target
   *     for bundling.
   *
   *   config (Object): Optional. Can be used to e.g. specify a
   *     onUploadProgress() callback.
   */
  uploadImage(
    title,
    uploadGroupId,
    categoryId,
    hardStart,
    hardExpire,
    file,
    reuploadSpotId = null,
    duration = null,
    tags = null,
    tagEntriesCanBeDeactivated = true,
    bundleMainTvSpotId = null,
    config = null
  ) {
    const url = 'upload/image';
    const formdata = new FormData();
    formdata.append('title', title);
    formdata.append('uploadgroup', uploadGroupId);
    formdata.append('category', categoryId);
    if (hardStart) {
      formdata.append('hardstartdate', hardStart);
    }
    if (hardExpire) {
      formdata.append('hardexpirationdate', hardExpire);
    }
    // Note: Passing the filename separately ensures that it is
    //   available in the backend even when 'file' is actually a Blob
    //   object e.g. due to Edge compatibility workarounds.
    //   See: https://stackoverflow.com/a/22624552
    formdata.append('f', file, file.name);

    if (reuploadSpotId) {
      formdata.append('reupload-spotid', reuploadSpotId);
    }
    if (duration) {
      formdata.append('duration', duration);
    }

    if (tags) {
      const tagIdsString = tags.map((tag) => String(tag.id)).join(',');
      formdata.append('tags', tagIdsString);
    }
    if (tagEntriesCanBeDeactivated) {
      formdata.append('tag_entries_can_be_deactivated', 'on');
    }

    if (bundleMainTvSpotId) {
      formdata.append('bundle_main_tvspot_id', bundleMainTvSpotId);
    }

    return API.post(url, formdata, config);
  }

  /**
   * Upload a video/audio file to the server.
   *
   * We are using the same endpoint here as used for internal upload,
   * just aliased via /api/v1. This can also be used to replace an
   * existing Spot, in that case 'reuploadSpotId' must be given.
   *
   * Args:
   *
   *   title (String): The title to use for the new TVSpot.
   *
   *   uploadGroupId (Number): The Group id to associate with.
   *
   *   categoryId (Number): The SpotCategory id to associate with.
   *
   *   hardStart (String): Start date of hard-range, can be null.
   *   hardExpire (String): End date of hard-range, can be null.
   *
   *   file (File): The file to upload.
   *
   *   reuploadSpotId (Number): Optional. Spot id to replace this with.
   *     Note: User is allowed to replace the Spot when its Group is one
   *     of his uploadgroups.
   *
   *   chosenTVFormats (Array[object]): Optional. A list of target
   *     TVFormat objects. We'll only submit their IDs in here.
   *
   *   tags (Array[object]): Optional list of Tags to assign to the new
   *     content. We'll only submit their IDs in here.
   *
   *   bundleMainTvSpotId (Number): Optional TVSpot ID to use as target
   *     for bundling.
   *
   *   config (Object): Optional. Can be used to e.g. specify a
   *     onUploadProgress() callback.
   */
  uploadVideoOrAudio(
    title,
    uploadGroupId,
    categoryId,
    hardStart,
    hardExpire,
    file,
    reuploadSpotId = null,
    chosenTVFormats = null,
    tags = null,
    tagEntriesCanBeDeactivated = true,
    bundleMainTvSpotId = null,
    config = null
  ) {
    const defaultSpotLifeTimeMonths = 11;

    const url = 'upload/videoaudio';
    const formdata = new FormData();
    formdata.append('title', title);
    formdata.append('uploadgroup', uploadGroupId);
    formdata.append('spotlifetime', defaultSpotLifeTimeMonths);
    formdata.append('spotcategory', categoryId);
    if (hardStart) {
      formdata.append('hardstartdate', hardStart);
    }
    if (hardExpire) {
      formdata.append('hardexpirationdate', hardExpire);
    }
    formdata.append('files[]', file);
    if (reuploadSpotId) {
      formdata.append('reupload-spotid', reuploadSpotId);
    }

    if (chosenTVFormats) {
      // Convert chosen TVFormats objects into a CSV ID string like: '1,2,3'.
      const chosenTVFormatIdsString = chosenTVFormats
        .map((tvformat) => String(tvformat.id))
        .join(',');
      formdata.append('chosen_tvformats', chosenTVFormatIdsString);
    }

    if (tags) {
      const tagIdsString = tags.map((tag) => String(tag.id)).join(',');
      formdata.append('tags', tagIdsString);
    }
    if (tagEntriesCanBeDeactivated) {
      formdata.append('tag_entries_can_be_deactivated', 'on');
    }

    if (bundleMainTvSpotId) {
      formdata.append('bundle_main_tvspot_id', bundleMainTvSpotId);
    }

    // We must pass these like it was read from a checkbox input,
    // omitting then would set internally_approved and published to False.
    formdata.append('internally_approved', 'on');
    formdata.append('published', 'on');

    return API.post(url, formdata, config);
  }

  listSpecialTVFormatsAllowedToUpload() {
    return API.get('upload/allowedtvformats');
  }

  listUserTVFormatsChoosable() {
    return API.get('usertvformats/choosable');
  }

  listUserTVFormatsAvailable() {
    return API.get('usertvformats/available');
  }

  updateChosenBrandGroups(ids) {
    const url = 'account/user/brandgroups';
    const payload = {
      chosen_brand_group_ids: ids.join(','),
    };
    const promise = API.put(url, payload);
    return promise;
  }

  /**
   * Partially update a User and its ClientUserProfile.
   */
  updateUserAccount(payload) {
    let url = 'account/user';
    let promise = API.patch(url, payload);
    return promise;
  }

  deleteUserAccount(email) {
    const url = 'account/user/delete';
    const payload = {email: email};
    return API.post(url, payload);
  }

  submitFirstloginForm(password1, password2, dsgvoAccepted) {
    const url = 'account/user/firstlogin';
    const payload = {
      password1: password1,
      password2: password2,
      dsgvo_accepted: dsgvoAccepted,
    };
    return API.post(url, payload);
  }

  acceptPrivacyPolicy() {
    let url = 'account/user/dsgvo/accept';
    let promise = API.post(url);
    return promise;
  }

  requestEmailChange(payload) {
    const url = 'account/user/email/request';
    return API.post(url, payload);
  }

  /**
   * Toggle user's subscription to a specific mailing type.
   *
   * Args:
   *   mailingType (String): One of: 'general', 'groups', 'campaigns', 'brands'.
   *   subscribed (Boolean)
   */
  updateMailSubscription(mailingType, subscribed) {
    const url = 'account/mailings/subscriptions';
    const keyMap = {
      general: 'receives_mailing_general_news',
      groups: 'receives_mailing_new_content_in_groups',
      campaigns: 'receives_mailing_new_content_campaigns',
      brands: 'receives_mailing_new_content_brands',
      docmgr: 'receives_mailing_new_content_docmgr',
    };

    const key = keyMap[mailingType];
    if (!key) {
      throw 'Unknown mailingType: ' + mailingType;
    }

    const payload = {};
    payload[key] = subscribed;

    return API.post(url, payload);
  }

  inviteNewUser(payload) {
    const url = 'management/users/invite';
    return API.post(url, payload);
  }

  listManagedUsers() {
    const url = 'management/users';
    return API.get(url);
  }

  listPendingUserInvitations() {
    const url = 'management/users/invitations';
    return API.get(url);
  }

  /**
   * Upload video from given TVSpot to a specific managed page on Facebook.
   *
   * That upload will create a copy of the video hosted on Facebook and
   * published on the User's managed page with given title and description.
   *
   * Args:
   *   tvspotId (Number)
   *   opts (Object):
   *     - pageAccessToken (String)
   *     - pageId (Number|String)
   *     - pageName (String)
   *     - title (String): Optional
   *     - description (String): Optional
   *     - scheduledPublishTime (Number): Optional (UNIX Epoch)
   *
   * Returns:
   *   Promise
   */
  shareVideoOnFacebookPage(tvspotId, opts) {
    const url = 'upload/facebook/video/' + tvspotId;
    const payload = {
      page_access_token: opts.pageAccessToken,
      page_id: opts.pageId,
      page_link: opts.pageLink,
      page_name: opts.pageName,
      description: opts.description || '',
    };
    if (opts.scheduledPublishTime) {
      payload.scheduled_publish_time = opts.scheduledPublishTime;
    }
    if (opts.overlayData) {
      payload.overlay_data = opts.overlayData;
    }
    return API.post(url, payload);
  }

  /**
   * Upload video from given TVSpot to Instagram account linked to Facebook page.
   *
   * That upload will create a copy of the video hosted on Instagram and
   * published on the User's Instagram feed.
   *
   * Args:
   *   tvspotId (Number)
   *   opts (Object):
   *     - userAccessToken (String)
   *     - instagramBusinessAccountId (Number|String)
   *     - caption (String): Optional
   *     - overlayData (Object): Optional
   *
   * Returns:
   *   Promise
   */
  shareVideoOnInstagram(tvspotId, opts) {
    const url = 'upload/instagram/video/' + tvspotId;
    const payload = {
      user_access_token: opts.userAccessToken,
      instagram_business_account_id: opts.instagramBusinessAccountId,
    };
    if (opts.caption) {
      payload.caption = opts.caption;
    }
    if (opts.overlayData) {
      payload.overlay_data = opts.overlayData;
    }
    return API.post(url, payload);
  }

  /**
   * Request a login-link via email since User forgot his password.
   *
   * This does not use token authentication and does not fallback on
   * 404, since it is a desired error code on this endpoint.
   */
  requestForgotPasswordLink(email) {
    const url = 'account/user/forgotpassword/request';
    const payload = {email: email};
    const opts = null;
    const tokenAuth = false;
    const fallbackOn404 = false;
    return API.request('post', url, payload, opts, tokenAuth, fallbackOn404);
  }

  retrieveDashboardInfo() {
    return this.get('dashboardinfo');
  }

  retrieveVisitorcountDashboardWidgetInfo() {
    return this.get('dashboardinfo/visitorcount');
  }

  /**
   * List Spots and Tickers that are available to current User.
   *
   * This endpoint is paginated. The request created here is explicitly
   * marked as being cancellable (these are the only requests so far
   * where we need this, otherwise we'd need multiple cancellation
   * tokens).
   *
   * Args:
   *   type (String): 'radio', 'tv' or 'any'.
   *   params (Object): Optional mapping of URL query parameters.
   */
  listUserContents(params = {}) {
    let url = 'usercontents';
    let promise = this.get(url, {
      params: params,
      allowCancellation: true,
    });
    return promise;
  }

  retrieveNumUnseenContents() {
    const url = 'usercontents/unseen';
    const promise = API.get(url);
    return promise;
  }

  /**
   * Track that user has just checked out contents/downloads.
   *
   * Args:
   *   resource (String): One of: 'content', 'downloads', 'sites'
   */
  updateUserLastSeen(resource) {
    const url = 'user/lastseen';
    // Build a datetime string in a format that Django understands.
    const now = dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss');
    const payload = {};
    payload['last_seen_' + resource] = now;
    const promise = API.patch(url, payload);
    return promise;
  }

  listUserTickers() {
    let url = 'usertickers';
    let promise = this.get(url);
    return promise;
  }

  listUserDownloadFiles(params = {}) {
    let url = 'userdownloadfiles';
    let promise = this.get(url, {params: params});
    return promise;
  }

  listUserSpotCategories() {
    const url = 'userspotcategories';
    const promise = this.get(url);
    return promise;
  }

  createUserSpotCategory(name, groupId) {
    const url = 'userspotcategories';
    const payload = {
      name: name,
      group_id: groupId,
    };
    const promise = this.post(url, payload);
    return promise;
  }

  updateUserSpotCategory(id, name, groupId) {
    const url = `userspotcategories/${id}`;
    const payload = {
      name: name,
      group_id: groupId,
    };
    const promise = this.patch(url, payload);
    return promise;
  }

  deleteUserSpotCategory(id, contentsAlso) {
    const url = `userspotcategories/${id}`;
    const payload = {
      contents_also: contentsAlso,
    };
    return this.request('delete', url, payload, null);
  }

  listChangelogEntries() {
    return API.get('changelog/entries');
  }

  listDashboardAdvertisements() {
    return API.get('dashboard/advertisements');
  }

  /**
   * User is interested in advertisement.
   */
  signalInterestForDashboardAdvertisement(advertId, message) {
    return API.post('dashboard/advertisements/' + advertId + '/interested', {
      message: message,
    });
  }

  /**
   * Use /usercontents endpoint to get the new radio and tv content.
   *
   * This allows us to reuse the existing access checks already
   * implemented there.
   *
   * When there is enough new content, we fetch what we can and the
   * dashboard may only show TV content or only radio content, so this
   * may look different than the fallback: Where there is no or not
   * enough new content, we fall back to listing some 'latest' at
   * least, so the list isn't empty. This will fetch 6 TV and 6 Radio
   * contents.
   */
  listLatestContents() {
    console.log('[DEBUG] Fetching newest content');
    let newContents = API.get('latestcontents');

    return new Promise((resolve, reject) => {
      newContents.then((response) => {
        let contents = response.data.results;
        if (contents.length > 6) {
          resolve(orderBy(contents, 'date', 'desc'));
        } else {
          // Fallback to 'newest'.
          console.log('[DEBUG] Fetching latest content');

          const radioPromise = API.get('usercontents', {
            params: {
              ctypes: 'radio',
              page_size: 6,
            },
          });

          const tvPromise = API.get('usercontents', {
            params: {
              ctypes: 'tv',
              page_size: 6,
            },
          });

          Promise.all([radioPromise, tvPromise]).then((responses) => {
            const radioContents = responses[0].data.results;
            const tvContents = responses[1].data.results;
            const contents = radioContents.concat(tvContents);
            resolve(contents);
          });
        }
      });
    });
  }

  getIsUsingWebview() {
    const url = 'webview';
    const tokenAuth = false;
    const promise = API.request('GET', url, null, null, tokenAuth);
    return promise;
  }

  submitUserFeedback(message) {
    let url = 'userfeedback';
    let payload = {message: message};
    let promise = API.post(url, payload);
    return promise;
  }

  submitSignUpForm(payload) {
    let url = 'signup';
    const tokenAuth = false;
    let promise = API.request('POST', url, payload, null, tokenAuth);
    return promise;
  }

  checkIsEmailDomainGeneric(email) {
    const url = 'signup/isemailgeneric';
    const tokenAuth = false;
    const params = {email: email};
    return API.request('GET', url, null, {params: params}, tokenAuth);
  }

  requestUploadFeature() {
    const url = 'userfeedback/requestupload';
    return API.post(url);
  }

  requestUsermanagementFeature() {
    const url = 'userfeedback/requestusermanagement';
    return API.post(url);
  }

  trackInstagramButtonClicked(tvspotId) {
    const url = 'userfeedback/instagram';
    const payload = {tvspot_id: tvspotId};
    return API.post(url, payload);
  }

  trackWifiSetupStarted() {
    const url = 'userfeedback/wifisetup';
    const payload = null;
    const opts = null;
    const tokenAuth = false;
    return API.request('post', url, payload, opts, tokenAuth);
  }

  getPasswordResetURL() {
    return environment.urls.passwordReset;
  }

  startOTP2FASetup() {
    const url = 'authenticationmethods/add';
    const payload = {
      method: 'otp',
    };
    return API.post(url, payload);
  }

  startSMS2FASetup(mobile_number) {
    const url = 'authenticationmethods/add';
    const payload = {
      method: 'sms',
      mobile_number: mobile_number,
    };
    return API.post(url, payload);
  }

  verify2FAMethod(method, code) {
    const url = 'authenticationmethods/' + method.id + '/verify';
    const payload = {
      code: code,
    };
    return API.post(url, payload);
  }

  disable2FAMethod(method) {
    const url = 'authenticationmethods/' + method.id;
    return API.delete(url);
  }

  /**
   * Perform an HTTP request, adding authentication by default.
   *
   * Optionally update the app progressbar to indicate loading.
   */
  request(method, url, payload, opts, tokenAuth = true, fallbackOn404 = true) {
    let self = this;

    if (!opts) {
      opts = {};
    }
    if (tokenAuth) {
      const token = store.getAuthToken();
      if (!token) {
        console.warn(
          '[WARN] ' +
            url +
            " requires authentication, but we don't have a token"
        );
        return new Promise((resolve, reject) => reject());
      }
    }

    let config = opts;
    config.url = this.apiURL + url;
    config.method = method;
    if (payload) {
      config.data = payload;
    }
    if (opts.allowCancellation) {
      config.cancelToken = this._cancellation.token;
    }

    const locale = store.getCurrentLocale();
    if (locale) {
      config.headers = {'X-App-Locale': locale};
    }

    let promise = axios.request(config);

    // Handle any 401 response by logging out and redirecting to login.
    promise.then(
      (response) => {},
      (error) => {
        handleHttpError(error);
      }
    );

    if (fallbackOn404) {
      promise.catch((error) => {
        goToFallbackOn404(error);
      });
    }

    if (this.updateProgress) {
      store.commit('SET_PROGRESS_INDETERMINATE', true);
      promise
        .then((response) => {
          store.commit('SET_PROGRESS_INDETERMINATE', false);
        })
        .catch((error) => {
          store.commit('SET_PROGRESS_INDETERMINATE', false);
        });
    }

    return promise;
  }

  cancelCurrentRequest() {
    if (this._cancellation) {
      this._cancellation.cancel();
    }
    // Use a different cancel token for the next request, otherwise
    // we'd cancel all requests (may not be what we want) and this
    // could also throw exceptions on requests that are already
    // finished.
    this._cancellation = this._createCancellation();
  }

  _createCancellation() {
    // https://github.com/mzabriskie/axios#cancellation
    let cancelSource = CancelToken.source();
    let cancellation = {
      token: cancelSource.token,
      cancel: cancelSource.cancel,
    };
    return cancellation;
  }

  /**
   * Allow to install this object as a Vue plugin.
   *
   * All Vue components will then be able to access it via: this.$api
   *
   * Use like:
   *     import 'API' from './API';
   *     Vue.use(API);
   */
  install(Vue, options) {
    Vue.prototype.$api = this;
  }
}

const API = new _API();
export default API;



// WEBPACK FOOTER //
// ./src/api.js